/**
 * The consent token the checkout relays across a domain boundary.
 *
 * When a partner journey ends on the partner's own site, the guest's decision
 * has to travel with them: the partner's tags are on a domain that cannot read
 * anything we recorded. The decision is forwarded as a short, opaque-looking but
 * self-describing token carrying only Google's consent mode v2 signals and the
 * moment the decision was taken — never an identifier, never a category name of
 * ours, never anything about the guest.
 *
 * The same token is read back on an inbound URL by `hostConsent.ts`, which
 * applies the safeguards. This module only encodes and decodes it, and refuses
 * anything it cannot fully account for.
 */
import type { ConsentCategories } from "../services/cookieConsent/types";
import { type ConsentSignalState, toGoogleConsentState } from "./consent";

/** URL parameter carrying the timestamped consent token. */
export const CONSENT_RELAY_PARAM = "gc_consent";

/** Token version. A token of any other version is not read. */
export const CONSENT_RELAY_VERSION = 1;

/**
 * Longest token value decoded. A version 1 token is around 150 characters, so
 * anything approaching this bound is not one of ours and is discarded unparsed.
 */
export const MAX_CONSENT_RELAY_LENGTH = 512;

/** The consent mode v2 signals the token carries, and the only ones it may. */
export const RELAYED_SIGNALS = ["analytics_storage", "ad_storage", "ad_user_data", "ad_personalization"] as const;

export type RelayedSignal = (typeof RELAYED_SIGNALS)[number];

/** A relayed decision, signal by signal. */
export type RelayedSignals = Record<RelayedSignal, ConsentSignalState>;

/** A decoded, structurally valid token. Whether it may be acted on is a separate question. */
export interface ConsentRelayToken {
  readonly version: number;
  /** Epoch milliseconds at which the decision was taken. */
  readonly decidedAt: number;
  readonly signals: RelayedSignals;
}

/** The wire shape, kept short because it travels on a URL. */
interface EncodedToken {
  readonly v: number;
  readonly ts: number;
  readonly s: Record<string, unknown>;
}

function toBase64Url(value: string): string {
  // Base64 padding is one or two characters, so bounding the quantifier keeps
  // the match linear: an unbounded `=+` backtracks over every start position.
  return btoa(value)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/={1,2}$/, "");
}

function fromBase64Url(value: string): string | null {
  const padding = (4 - (value.length % 4)) % 4;

  try {
    return atob(`${value.replace(/-/g, "+").replace(/_/g, "/")}${"=".repeat(padding)}`);
  } catch {
    return null;
  }
}

/**
 * Encodes the visitor's decision for the partner.
 *
 * Only the four advertising and measurement signals are forwarded: they are the
 * ones a partner's Google tags act on, and they say nothing about the guest.
 */
export function encodeConsentRelayToken(categories: ConsentCategories, decidedAt: number = Date.now()): string {
  const google = toGoogleConsentState(categories);
  const signals: Record<string, ConsentSignalState> = {};
  for (const signal of RELAYED_SIGNALS) {
    signals[signal] = google[signal];
  }

  const payload: EncodedToken = { v: CONSENT_RELAY_VERSION, ts: Math.trunc(decidedAt), s: signals };

  return toBase64Url(JSON.stringify(payload));
}

/** Only an explicit grant is a grant; anything else, absent included, is a refusal. */
function readSignal(value: unknown): ConsentSignalState {
  return value === "granted" ? "granted" : "denied";
}

/**
 * Epoch milliseconds a decision could plausibly carry: this millennium, and not
 * beyond a century of it.
 *
 * The lower bound is what rejects a timestamp given in seconds, which would
 * otherwise read as 1970 and be judged stale rather than malformed; the upper
 * bound rejects a value too large to be a date at all.
 */
const EARLIEST_PLAUSIBLE_DECISION_MS = 1_000_000_000_000;
const LATEST_PLAUSIBLE_DECISION_MS = 4_000_000_000_000;

function isPlausibleTimestamp(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value >= EARLIEST_PLAUSIBLE_DECISION_MS &&
    value <= LATEST_PLAUSIBLE_DECISION_MS
  );
}

/**
 * Decodes a relayed token.
 *
 * @returns the token, or `null` when the value is oversized, not base64url, not
 * JSON, of an unknown version, or carries no usable timestamp — every one of
 * which is treated as a malformed relay by the caller, and so as a refusal.
 */
export function decodeConsentRelayToken(raw: string): ConsentRelayToken | null {
  if (!raw || raw.length > MAX_CONSENT_RELAY_LENGTH) return null;
  // Bounded, single-character class: no backtracking is possible.
  if (!/^[A-Za-z0-9_-]+$/.test(raw)) return null;

  const decoded = fromBase64Url(raw);
  if (decoded === null) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(decoded);
  } catch {
    return null;
  }

  if (typeof parsed !== "object" || parsed === null) return null;

  const { v, ts, s } = parsed as Partial<EncodedToken>;
  if (v !== CONSENT_RELAY_VERSION) return null;
  if (!isPlausibleTimestamp(ts)) return null;
  if (typeof s !== "object" || s === null) return null;

  const signals = {} as Record<RelayedSignal, ConsentSignalState>;
  for (const signal of RELAYED_SIGNALS) {
    signals[signal] = readSignal(s[signal]);
  }

  return { version: v, decidedAt: ts, signals };
}
